/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, { useCallback, useContext } from 'react';
import { Col, Row } from 'react-styled-flexboxgrid';
import debounce from 'lodash.debounce';
import { bool, number, oneOfType, shape, string } from 'prop-types';
import styled from 'styled-components';
import { StoreContext } from '../../context/store-context.jsx';
import MinusIcon from '../../img/minus.svg';
import PlusIcon from '../../img/plus.svg';
import {
  bodyMBoldBrownStyles,
  bodySBoldBrownStyles,
  bodyXsRegularBrownStyles,
  bodyXxsRegularBrownStyles,
  eyebrowSStyles,
  eyebrowXsStyles,
} from '../../styles/typography.js';
import { bpWidth, colors } from '../../styles/variables.js';
import { currencyFormatter } from '../../utils/currencyFormatter';

const LineItemRow = styled(Row)`
  padding-top: ${(props) => (props.firstItem ? '50px' : '40px')};
  padding-bottom: 10px;
  margin-bottom: 10px;
  border-bottom: 1px solid ${colors.gray100};

  @media (${bpWidth.mobile}) {
    padding-top: 10px;
  }
`;

const LineItemCol = styled(Col)``;

const ProductDetailsRow = styled(Row)``;

const QuantityAndSubtotalRow = styled(Row)`
  display: flex;
  justify-content: space-between;
  padding-top: 30px;

  @media (${bpWidth.mobile}) {
    padding-top: 20px;
  }
`;

const ProductName = styled.h2`
  ${eyebrowSStyles};

  i {
    font-style: italic;
  }

  @media (${bpWidth.mobile}) {
    ${eyebrowXsStyles};

    i {
      font-style: italic;
    }
  }
`;

const VariantName = styled.p`
  padding-top: 4px;
  ${bodyXsRegularBrownStyles};

  @media (${bpWidth.mobile}) {
    ${bodyXxsRegularBrownStyles};
  }
`;

const ShippingText = styled.p`
  ${bodyXsRegularBrownStyles};
  padding-top: 10px;
  color: ${colors.gray300};

  @media (${bpWidth.mobile}) {
    ${bodyXxsRegularBrownStyles};
    color: ${colors.gray300};
  }
`;

const IncrementAndDecrementRow = styled(Row)`
  align-items: center;
`;

const IncrementAndDecrementContainer = styled.div`
  margin-right: 20px;
  display: flex;
  align-items: center;
`;

const LineItemQuantityText = styled.p`
  ${bodyXsRegularBrownStyles};
  display: inline-block;
  width: 60px;
  text-align: center;

  @media (${bpWidth.mobile}) {
    ${bodyXxsRegularBrownStyles};
  }
`;

const RemoveText = styled.p`
  ${bodyXsRegularBrownStyles};
  color: ${colors.gray600};
  cursor: pointer;
  display: inline-block;
  text-align: right;
  text-decoration: underline;

  @media (${bpWidth.mobile}) {
    ${bodyXxsRegularBrownStyles};
    color: ${colors.gray00};
  }
`;

const LineItemSubtotal = styled.p`
  ${bodyMBoldBrownStyles};
  letter-spacing: -0.16px;
  text-align: right;

  @media (${bpWidth.mobile}) {
    ${bodySBoldBrownStyles};
  }
`;

const LineItemImage = styled.img`
  width: 100%;
  max-width: 100%;
`;

const QtyButton = styled.button`
  background: none;
  border: 0 none;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;

  ${(props) => (props.align ? `justify-content: ${props.align};` : '')}
`;

const LineItem = React.forwardRef((props) => {
  const { lineItemData, index, arrayLength, accessoriesExpanded } = props;

  const { removeLineItem, updateLineItem } = useContext(StoreContext);

  const [quantity, setQuantity] = React.useState(lineItemData.quantity);

  const firstItem = index === 0;
  const lastItem = index === arrayLength - 1;
  const isgitaProduct = !!(lineItemData.title === 'gitaPLUS' || lineItemData.title === 'gita mini');

  const handleRemove = () => {
    removeLineItem(lineItemData.id);
  };

  const uli = debounce((value) => updateLineItem(lineItemData.id, value), 300);
  const debouncedUli = useCallback((value) => uli(value), [uli]);

  const handleQuantityChange = (value) => {
    if (value !== '' && Number(value) < 1) {
      return;
    }
    setQuantity(value);
    if (Number(value) >= 1) {
      debouncedUli(value);
    }
  };

  const doIncrement = () => {
    handleQuantityChange(Number(quantity || 0) + 1);
  };

  const doDecrement = () => {
    handleQuantityChange(Number(quantity || 0) - 1);
  };

  return (
    <LineItemRow firstItem={firstItem} lastItem={lastItem} accessoriesExpanded={accessoriesExpanded}>
      <LineItemCol xs={8} sm={8} md={10} lg={12}>
        <ProductDetailsRow>
          <Col xs={4} sm={4} md={6} lg={7}>
            <ProductName>
              {lineItemData.title === 'gita mini' ? (
                <span>
                  gita
                  <i>mini</i>
                </span>
              ) : (
                <span>{lineItemData.title}</span>
              )}
            </ProductName>
            {lineItemData.variant.title !== 'Default Title' && <VariantName>{lineItemData.variant.title}</VariantName>}
            <ShippingText>
              {isgitaProduct ? 'Includes free shipping and charger.' : 'Includes free shipping.'}
            </ShippingText>
          </Col>
          <Col xs={4} sm={4} md={4} lg={5}>
            <Row>
              <Col xsOffset={3} xs={5} smOffset={3} sm={5} mdOffset={1} md={9} lgOffset={2} lg={10}>
                <LineItemImage src={lineItemData.variant.image.src} />
              </Col>
            </Row>
          </Col>
        </ProductDetailsRow>
        <QuantityAndSubtotalRow>
          <div>
            <IncrementAndDecrementRow>
              <IncrementAndDecrementContainer>
                <QtyButton onClick={doDecrement}>
                  <MinusIcon
                    style={{
                      cursor: 'pointer',
                      color: '#022137',
                      verticalAlign: 'middle',
                      width: '12px',
                      height: 'auto',
                    }}
                  />
                </QtyButton>
                <LineItemQuantityText>{lineItemData.quantity}</LineItemQuantityText>
                <QtyButton onClick={doIncrement} align="right">
                  <PlusIcon
                    style={{
                      cursor: 'pointer',
                      color: '#022137',
                      verticalAlign: 'middle',
                      width: '12px',
                      height: 'auto',
                    }}
                  />
                </QtyButton>
              </IncrementAndDecrementContainer>
              <RemoveText onClick={handleRemove}>Remove</RemoveText>
            </IncrementAndDecrementRow>
          </div>
          <div>
            <LineItemSubtotal>
              {currencyFormatter(lineItemData.variant.priceV2.amount * lineItemData.quantity, {
                currency: lineItemData.variant.priceV2.currencyCode,
              })}
            </LineItemSubtotal>
          </div>
        </QuantityAndSubtotalRow>
      </LineItemCol>
    </LineItemRow>
  );
});

LineItem.propTypes = {
  accessoriesExpanded: bool,
  lineItemData: shape({
    quantity: number,
    title: string,
    id: string,
    variant: shape({
      title: string,
      image: shape({
        src: string,
      }),
      priceV2: shape({
        amount: oneOfType([number, string]),
        currencyCode: string,
      }),
    }),
  }),
};
LineItem.defaultProps = {
  accessoriesExpanded: false,
};

export default LineItem;
